/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';

/**
 * @prop {String} mobile - Defines mobile view.
 * @prop {Boolean} functions - Holds sh-menu-item for power-related functions such as Lock System, Restart, Shut Down, etc..
 * @slot - Holds sh-home-cards.
 * @slot functions - Holds Menu Items related to power functions such as Lock System, Restart, Shut Down, etc.
 */

export class SHHomeScreen extends LitElement {
  static get properties() {
    return {
      functions: { type: Boolean, reflect: true },
      mobile: { type: Boolean, reflect: true },
      _iconVisible: { type: Boolean, reflect: false },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .cards-wrapper {
          display: flex;
          flex-wrap: wrap;
          min-height: fit-content;
          width: fit-content;
          max-height: calc(100% + 1rem);
          max-width: calc(100% + 1rem);
          margin: -0.5rem;
          justify-content: center;
          align-items: center;
        }

        #functions-button {
          position: fixed;
          bottom: 1rem;
          right: 1rem;
        }

        .functions-card:not([active]) {
          opacity: 0;
          margin-bottom: -1rem;
          pointer-events: none;
        }

        .functions-card {
          transition: var(--transition-time) all ease-in-out;
          position: fixed;
          right: 1rem;
          bottom: 3.5rem;
          width: 15rem;
        }
        .functions-div {
          margin: -0.5rem 0;
        }

        /* small screens */
        :host([mobile]) {
          align-items: flex-start;
        }
        :host([mobile]) .cards-wrapper {
          width: calc(100% + 1rem);
          flex-direction: column;
        }
        :host([mobile]) .cards-wrapper > ::slotted(sh-home-card) {
          width: calc(100% - 1rem);
        }
      `,
    ];
  }

  render() {
    return html` <div class="cards-wrapper">
        <slot></slot>
      </div>
      ${this._iconVisible
        ? html` <sh-icon
            button
            icon="standby"
            id="functions-button"
            @click="${this._handleFunctions}"
          ></sh-icon>`
        : ''}
      <sh-card class="functions-card" ?active="${this.functions}">
        <div class="functions-div" @click="${this._handleFunctions}">
          <slot
            name="functions"
            @slotchange=${(e) => {
              this.slotChanged(e.target);
            }}
          ></slot>
        </div>
      </sh-card>`;
  }

  slotChanged(slot) {
    const nodes = slot.assignedNodes({
      flatten: true,
    }).length;
    if (nodes > 0) {
      this._iconVisible = true;
    } else {
      this._iconVisible = false;
      this.functions = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    /** @ignore */
    this.windowDimensionObserver = this.handleResize.bind(this);
    window.addEventListener('resize', this.windowDimensionObserver);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.windowDimensionObserver);
  }
  updated(changedProperties) {
    const listOfProperties = ['mobile'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  handleResize() {
    this.mobile = document.documentElement.clientWidth < 767;
  }

  _handleFunctions() {
    this.functions = !this.functions;
  }
}
customElements.define('sh-home-screen', SHHomeScreen);
